import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { FormLayout } from 'components/FormLayout/FormLayout';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { NewPasswordRequirementDescription } from 'pages/Me/UserProfile/NewPasswordRequirementDescription';
import { validateUserPassword } from 'pages/Settings/Administration/LocalUsers/helpers/validateUserPassword';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

type Props = {
  className?: string;
  editedUserId: string;
};

export type UpdatePasswordFormValues = {
  newPassword: string;
  retypePassword: string;
};

type UpdatePasswordFormValuesWithAdminPassword = UpdatePasswordFormValues & {
  adminPassword?: string;
};

export const UpdatePasswordForm: FC<Props> = (props) => {
  const [submitting, setSubmitting] = useLoadingState();
  const localUsers = useLocale().localUsers;
  const formMsg = localUsers.form;
  const adminPasswordMsg = localUsers.adminPassword;
  const api = useApi();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const { dispatch, isCurrentUserLocal, ldapId } =
    useGlobalStateContextSelectors({
      dispatch: prop('dispatch'),
      isCurrentUserLocal: prop('user', 'isLocal'),
      ldapId: prop('user', 'ldapId'),
    });
  const requiresAdminPassword = isCurrentUserLocal || Boolean(ldapId);

  const handleSubmit = async (
    values: UpdatePasswordFormValuesWithAdminPassword,
  ) => {
    await withLoadingIndicator(setSubmitting)(async () => {
      try {
        await api.request({
          url: `/users/${props.editedUserId}/password`,
          method: 'POST',
          data: {
            password: values.newPassword,
            ...(requiresAdminPassword && {
              adminPassword: values.adminPassword,
            }),
          },
        });
        notifications.showNotification({
          type: 'success',
          message: formMsg.passwordUpdated,
        });
      } catch (error: unknown) {
        if (
          requiresAdminPassword &&
          isIpfError(error, 'API') &&
          error?.payload?.response.code === 'API_INVALID_ADMIN_PASSWORD'
        ) {
          notifications.showNotification({
            message: adminPasswordMsg.error,
            title: adminPasswordMsg.title,
            type: 'danger',
          });
          // Set loginStatus to LOGGED_OUT so the logout command skips the API call
          // (session is already invalidated server-side)
          dispatch((ctx) => () => {
            ctx.updateState((draft) => {
              draft.loginStatus = 'LOGGED_OUT';
            });
          });
          navigate('/logout');
          return;
        }

        notifications.showNotification({
          message: 'Unknown error',
          title: adminPasswordMsg.title,
          type: 'danger',
        });
      }
    });
  };

  const validate = (
    values: UpdatePasswordFormValuesWithAdminPassword,
  ): FormikErrors<UpdatePasswordFormValuesWithAdminPassword> => {
    const errors: FormikErrors<UpdatePasswordFormValuesWithAdminPassword> = {};
    if (requiresAdminPassword && values.adminPassword === '') {
      errors.adminPassword = formMsg.validations.required;
    }
    validateUserPassword(values, errors, formMsg.validations);

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      ...(requiresAdminPassword && { adminPassword: '' }),
      newPassword: '',
      retypePassword: '',
    },
    validate,
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  return (
    <form onSubmit={formik.handleSubmit} className={props.className}>
      <FormLayout width='full' noPadding>
        <header>
          <h2>{formMsg.updatePasswordTitle}</h2>
        </header>
        <Message
          className='mb max-w-96'
          variant='plain'
          type='info'
          message={formMsg.passwordChangeInfo}
        />
        {requiresAdminPassword && (
          <FormikTextField
            className='max-w-96'
            name='adminPassword'
            formik={formik}
            type='password'
            label={formMsg.fields.adminPassword}
            description={formMsg.fields.adminPasswordDescription}
          />
        )}
        <FormikTextField
          className='max-w-96'
          name='newPassword'
          formik={formik}
          type='password'
          label={formMsg.fields.newPassword}
          description={<NewPasswordRequirementDescription />}
        />
        <FormikTextField
          className='max-w-96'
          name='retypePassword'
          formik={formik}
          type='password'
          label={formMsg.fields.retypePassword}
        />
        <footer>
          <Button
            loading={submitting === 'LOADING'}
            type='submit'
            variant='primary'
            size='sm'
            icon='fa-floppy-disk'
          >
            {formMsg.updatePasswordButton}
          </Button>
        </footer>
      </FormLayout>
    </form>
  );
};
